import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  doc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  getDoc 
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../auth/components/AuthProvider';
import LotesConcesionariaService from '../../empresa/services/LotesConcesionariaService';
import ConcesionariaService from '../../empresa/services/ConcesionariaService';
import { formatearFecha } from '../../../utils/dateUtils';
import { TIPO_VEHICULO, ESTADO_PERFIL_VEHICULO, DESCRIPCION_ESTADO_PERFIL, COLOR_ESTADO_PERFIL } from '../../../constants/concesionariaConstants';

export default function GestionConcesionarias() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [concesionarias, setConcesionarias] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [solicitudesAgente, setSolicitudesAgente] = useState([]);
  const [solicitudesLotes, setSolicitudesLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrandoModalCrearLote, setMostrandoModalCrearLote] = useState(false);
  const [mostrandoModalAsignarAgente, setMostrandoModalAsignarAgente] = useState(false);
  const [mostrandoModalDetalle, setMostrandoModalDetalle] = useState(false);
  const [mostrandoModalGestionLote, setMostrandoModalGestionLote] = useState(false);
  const [mostrandoModalEditarConcesionaria, setMostrandoModalEditarConcesionaria] = useState(false);
  const [mostrandoModalNotas, setMostrandoModalNotas] = useState(false);
  const [mostrandoModalHistorial, setMostrandoModalHistorial] = useState(false);
  const [mostrandoModalBulkAsignar, setMostrandoModalBulkAsignar] = useState(false);
  const [mostrandoModalCrearPerfil, setMostrandoModalCrearPerfil] = useState(false);
  const [perfilesDisponiblesInfo, setPerfilesDisponiblesInfo] = useState(null);
  const [agenteBulkSeleccionado, setAgenteBulkSeleccionado] = useState('');
  const [solicitudLoteSeleccionada, setSolicitudLoteSeleccionada] = useState(null);
  const [formPerfilVehiculo, setFormPerfilVehiculo] = useState({
    marca: '',
    modelo: '',
    año: new Date().getFullYear(),
    tipoVehiculo: 'sedan',
    vin: ''
  });
  const [concesionariaSeleccionada, setConcesionariaSeleccionada] = useState(null);
  const [lotesConcesionaria, setLotesConcesionaria] = useState([]);
  const [tabActual, setTabActual] = useState(() => {
    // Leer el parámetro 'tab' de la URL al inicializar
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl || 'concesionarias';
  });
  const [clientesConcesionarias, setClientesConcesionarias] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [perfilesConcesionaria, setPerfilesConcesionaria] = useState([]);
  const [loadingPerfiles, setLoadingPerfiles] = useState(false);
  const [filtroBusqueda, setFiltroBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [concesionariasSeleccionadas, setConcesionariasSeleccionadas] = useState([]);
  const [notificaciones, setNotificaciones] = useState([]);
  const [formEditarConcesionaria, setFormEditarConcesionaria] = useState({
    nombre: '',
    email: '',
    telefono: '',
    direccion: '',
    activo: true,
    notas_admin: ''
  });
  
  // Formulario crear lote (admin puede crear sin agente)
  const [formLote, setFormLote] = useState({
    concesionariaId: '',
    agenteId: '',
    cantidad: 50,
    precioTotal: '',
    metodoPago: '',
    notas: ''
  });
  
  // Formulario asignar agente
  const [formAsignarAgente, setFormAsignarAgente] = useState({
    concesionariaId: '',
    agenteId: ''
  });

  useEffect(() => {
    cargarDatos();
    if (tabActual === 'clientes') {
      cargarClientesConcesionarias();
    }
  }, []);

  useEffect(() => {
    if (tabActual === 'clientes') {
      cargarClientesConcesionarias();
    }
  }, [tabActual]);

  // Sincronizar tab con parámetros de URL
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== tabActual) {
      setTabActual(tabFromUrl);
    }
  }, [searchParams]);

  // Actualizar URL cuando cambia el tab
  const cambiarTab = (nuevoTab) => {
    setTabActual(nuevoTab);
    setSearchParams({ tab: nuevoTab });
  };

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Cargar concesionarias desde empresas (tipoEmpresa === 'concesionaria')
      const empresasQuery = query(
        collection(db, 'empresas'),
        where('tipoEmpresa', '==', 'concesionaria')
      );
      const empresasSnap = await getDocs(empresasQuery);
      const concesionariasData = empresasSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // También cargar desde colección concesionarias (fallback)
      const concesionariasSnap = await getDocs(collection(db, 'concesionarias'));
      const concesionariasExtra = concesionariasSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Combinar y eliminar duplicados
      const todasConcesionarias = [...concesionariasData];
      concesionariasExtra.forEach(conc => {
        if (!todasConcesionarias.find(c => c.id === conc.id && c.email === conc.email)) {
          todasConcesionarias.push(conc);
        }
      });
      
      setConcesionarias(todasConcesionarias);
      
      // Cargar agentes
      const agentesSnap = await getDocs(collection(db, 'agentes'));
      const agentesData = agentesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAgentes(agentesData.filter(a => a.activo !== false));
      
      // Cargar solicitudes de agentes pendientes
      const solicitudesQuery = query(
        collection(db, 'solicitudes_agente'),
        where('estado', '==', 'pendiente'),
        where('tipoSolicitud', '==', 'asignar_agente')
      );
      const solicitudesSnap = await getDocs(solicitudesQuery);
      const solicitudesData = solicitudesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSolicitudesAgente(solicitudesData);

      // Cargar solicitudes de lotes (todas para admin)
      try {
        const solicitudesLotesQuery = query(
          collection(db, 'solicitudes_lotes'),
          orderBy('fechaSolicitud', 'desc')
        );
        const solicitudesLotesSnap = await getDocs(solicitudesLotesQuery);
        const solicitudesLotesData = solicitudesLotesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSolicitudesLotes(solicitudesLotesData);
      } catch (error) {
        console.error('Error cargando solicitudes de lotes con orderBy:', error);
        // Fallback sin orderBy
        try {
          const solicitudesLotesSnap = await getDocs(collection(db, 'solicitudes_lotes'));
          const solicitudesLotesData = solicitudesLotesSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Ordenar manualmente por fecha
          solicitudesLotesData.sort((a, b) => {
            const fechaA = a.fechaSolicitud?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
            const fechaB = b.fechaSolicitud?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
            return fechaB - fechaA;
          });
          setSolicitudesLotes(solicitudesLotesData);
        } catch (error2) {
          console.error('Error cargando solicitudes de lotes (fallback):', error2);
          setSolicitudesLotes([]);
        }
      }
      
    } catch (error) {
      console.error('Error cargando datos:', error);
      alert('Error al cargar datos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarLotesConcesionaria = async (concesionariaId) => {
    try {
      const lotes = await LotesConcesionariaService.obtenerLotes(concesionariaId);
      setLotesConcesionaria(lotes);
    } catch (error) {
      console.error('Error cargando lotes:', error);
      setLotesConcesionaria([]);
    }
  };

  const cargarClientesConcesionarias = async () => {
    setLoadingClientes(true);
    try {
      const clientesMap = new Map(); // Para evitar duplicados

      // 1. Buscar en perfiles_vehiculos_concesionaria donde hay clienteId
      const perfilesQuery = query(
        collection(db, 'perfiles_vehiculos_concesionaria'),
        where('clienteId', '!=', null)
      );
      const perfilesSnap = await getDocs(perfilesQuery);
      
      perfilesSnap.forEach(doc => {
        const perfil = doc.data();
        if (perfil.clienteId && perfil.concesionariaId) {
          const key = `${perfil.concesionariaId}_${perfil.clienteId}`;
          if (!clientesMap.has(key)) {
            clientesMap.set(key, {
              clienteId: perfil.clienteId,
              clienteNombre: perfil.clienteNombre || 'Sin nombre',
              clienteEmail: perfil.clienteEmail || '',
              clienteTelefono: perfil.clienteTelefono || '',
              concesionariaId: perfil.concesionariaId,
              concesionariaNombre: perfil.concesionariaNombre || 'Sin nombre',
              perfilVehiculoId: doc.id,
              marca: perfil.marca,
              modelo: perfil.modelo,
              año: perfil.año,
              estadoPerfil: perfil.estado,
              membresiaPremiumActiva: perfil.membresiaPremiumActiva || false,
              fechaAsignacion: perfil.fechaAsignacion,
              fechaActivacion: perfil.fechaActivacion
            });
          }
        }
      });

      // 2. Buscar en usuarios donde creadoPorConcesionaria es true para obtener datos adicionales
      const usuariosQuery = query(
        collection(db, 'usuarios'),
        where('creadoPorConcesionaria', '==', true)
      );
      const usuariosSnap = await getDocs(usuariosQuery);
      
      usuariosSnap.forEach(doc => {
        const usuario = doc.data();
        if (usuario.concesionariaId && usuario.uid) {
          const key = `${usuario.concesionariaId}_${usuario.uid}`;
          if (clientesMap.has(key)) {
            // Actualizar datos del cliente existente
            const clienteExistente = clientesMap.get(key);
            clienteExistente.nombre = usuario.nombre || clienteExistente.clienteNombre;
            clienteExistente.email = usuario.email || clienteExistente.clienteEmail;
            clienteExistente.telefono = usuario.telefono || clienteExistente.clienteTelefono;
            clienteExistente.tienePerfilPremium = usuario.tienePerfilPremium || false;
            clienteExistente.perfilesPremium = usuario.perfilesPremium || [];
          } else {
            // Crear nuevo registro si no existe en perfiles_vehiculos
            clientesMap.set(key, {
              clienteId: usuario.uid,
              clienteNombre: usuario.nombre || 'Sin nombre',
              clienteEmail: usuario.email || '',
              clienteTelefono: usuario.telefono || '',
              concesionariaId: usuario.concesionariaId,
              concesionariaNombre: usuario.concesionariaNombre || 'Sin nombre',
              tienePerfilPremium: usuario.tienePerfilPremium || false,
              perfilesPremium: usuario.perfilesPremium || []
            });
          }
        }
      });

      // 3. Buscar en perfiles_clientes para obtener datos completos del perfil
      const perfilesClientesQuery = query(
        collection(db, 'perfiles_clientes'),
        where('creadoPorConcesionaria', '==', true)
      );
      const perfilesClientesSnap = await getDocs(perfilesClientesQuery);
      
      perfilesClientesSnap.forEach(doc => {
        const perfilCliente = doc.data();
        if (perfilCliente.concesionariaId && perfilCliente.uid) {
          const key = `${perfilCliente.concesionariaId}_${perfilCliente.uid}`;
          if (clientesMap.has(key)) {
            // Actualizar con datos del perfil
            const clienteExistente = clientesMap.get(key);
            clienteExistente.perfilClienteId = doc.id;
            clienteExistente.plan = perfilCliente.plan;
            clienteExistente.membresia = perfilCliente.membresia;
            clienteExistente.estado = perfilCliente.estado;
            clienteExistente.fechaRegistro = perfilCliente.fechaRegistro;
          }
        }
      });

      // Convertir Map a Array y ordenar por concesionaria y luego por nombre
      const clientesArray = Array.from(clientesMap.values());
      clientesArray.sort((a, b) => {
        if (a.concesionariaNombre !== b.concesionariaNombre) {
          return a.concesionariaNombre.localeCompare(b.concesionariaNombre);
        }
        return (a.clienteNombre || '').localeCompare(b.clienteNombre || '');
      });

      setClientesConcesionarias(clientesArray);
    } catch (error) {
      console.error('Error cargando clientes de concesionarias:', error);
      mostrarNotificacion('Error al cargar clientes: ' + error.message, 'error');
      setClientesConcesionarias([]);
    } finally {
      setLoadingClientes(false);
    }
  };

  const handleCrearLote = async (e) => {
    e.preventDefault();
    
    try {
      if (!formLote.concesionariaId || !formLote.cantidad) {
        alert('Por favor completa todos los campos obligatorios');
        return;
      }
      
      setProcesando(true);
      
      // Admin puede crear lotes sin agente (agenteId es opcional)
      const agenteId = formLote.agenteId || 'admin_sistema';
      
      await LotesConcesionariaService.crearLote(
        formLote.concesionariaId,
        agenteId,
        parseInt(formLote.cantidad),
        {
          precioTotal: formLote.precioTotal ? parseFloat(formLote.precioTotal) : null,
          metodoPago: formLote.metodoPago || null,
          notas: (formLote.notas || '') + ' (Creado por Admin)'
        }
      );
      
      alert('✅ Lote creado exitosamente');
      
      // Limpiar formulario
      setFormLote({
        concesionariaId: '',
        agenteId: '',
        cantidad: 50,
        precioTotal: '',
        metodoPago: '',
        notas: ''
      });
      setMostrandoModalCrearLote(false);
      setConcesionariaSeleccionada(null);
      
      // Recargar datos
      await cargarDatos();
      
    } catch (error) {
      console.error('Error creando lote:', error);
      alert('Error al crear lote: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  const handleAprobarSolicitudLote = async (solicitud) => {
    try {
      if (!confirm(`¿Aprobar la solicitud de lote "${solicitud.nombreLote}" para ${solicitud.concesionariaNombre}?`)) {
        return;
      }

      setProcesando(true);

      // Crear el lote desde la solicitud
      await LotesConcesionariaService.crearLote(
        solicitud.concesionariaId,
        solicitud.agenteId || 'admin_sistema',
        solicitud.cantidad,
        {
          precioTotal: solicitud.precio || null,
          metodoPago: solicitud.metodoPago || null,
          notas: `Aprobado desde solicitud ${solicitud.id}`,
          fechaVenta: solicitud.fechaSolicitud || serverTimestamp()
        }
      );

      // Actualizar la solicitud como aprobada
      await updateDoc(doc(db, 'solicitudes_lotes', solicitud.id), {
        estado: 'aprobada',
        fechaAprobacion: serverTimestamp(),
        aprobadoPor: user?.email || 'admin',
        loteId: 'creado' // Se puede mejorar guardando el ID del lote creado
      });

      alert('✅ Solicitud aprobada y lote creado exitosamente');
      
      // Recargar datos
      await cargarDatos();
      setMostrandoModalGestionLote(false);
      setSolicitudLoteSeleccionada(null);
      
    } catch (error) {
      console.error('Error aprobando solicitud:', error);
      alert('Error al aprobar solicitud: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  const handleRechazarSolicitudLote = async (solicitud) => {
    try {
      const motivo = prompt('Ingresa el motivo del rechazo:');
      if (!motivo) return;

      setProcesando(true);

      await updateDoc(doc(db, 'solicitudes_lotes', solicitud.id), {
        estado: 'rechazada',
        fechaRechazo: serverTimestamp(),
        motivoRechazo: motivo,
        rechazadoPor: user?.email || 'admin'
      });

      alert('✅ Solicitud rechazada');
      
      // Recargar datos
      await cargarDatos();
      setMostrandoModalGestionLote(false);
      setSolicitudLoteSeleccionada(null);
      
    } catch (error) {
      console.error('Error rechazando solicitud:', error);
      alert('Error al rechazar solicitud: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  const handleAsignarAgente = async (e) => {
    e.preventDefault();
    
    try {
      if (!formAsignarAgente.concesionariaId || !formAsignarAgente.agenteId) {
        mostrarNotificacion('Por favor selecciona concesionaria y agente', 'warning');
        return;
      }
      
      setProcesando(true);
      
      // Actualizar empresa con agente asignado
      const empresaRef = doc(db, 'empresas', formAsignarAgente.concesionariaId);
      await updateDoc(empresaRef, {
        agenteAsignado: formAsignarAgente.agenteId,
        agente_id: formAsignarAgente.agenteId,
        fechaActualizacion: serverTimestamp(),
        historial_cambios: {
          ...(await getDoc(empresaRef)).data()?.historial_cambios || {},
          [`agente_asignado_${Date.now()}`]: {
            accion: 'asignacion_agente',
            agenteId: formAsignarAgente.agenteId,
            agenteNombre: agentes.find(a => a.id === formAsignarAgente.agenteId)?.nombre || 'N/A',
            fecha: serverTimestamp(),
            usuario: user?.email || 'admin'
          }
        }
      });
      
      // Si existe en colección concesionarias, actualizar también
      try {
        const concesionariaRef = doc(db, 'concesionarias', formAsignarAgente.concesionariaId);
        const concesionariaSnap = await getDoc(concesionariaRef);
        if (concesionariaSnap.exists()) {
          await updateDoc(concesionariaRef, {
            agenteAsignado: formAsignarAgente.agenteId,
            fechaActualizacion: serverTimestamp()
          });
        }
      } catch (err) {
        console.log('No se encontró en colección concesionarias:', err);
      }
      
      // Si hay solicitud pendiente, marcarla como resuelta
      const solicitudPendiente = solicitudesAgente.find(
        s => s.concesionariaId === formAsignarAgente.concesionariaId
      );
      if (solicitudPendiente) {
        await updateDoc(doc(db, 'solicitudes_agente', solicitudPendiente.id), {
          estado: 'resuelta',
          agenteAsignado: formAsignarAgente.agenteId,
          fechaResolucion: serverTimestamp()
        });
      }
      
      mostrarNotificacion('✅ Agente asignado exitosamente', 'success');
      
      // Limpiar formulario
      setFormAsignarAgente({
        concesionariaId: '',
        agenteId: ''
      });
      setMostrandoModalAsignarAgente(false);
      
      // Recargar datos
      await cargarDatos();
      
    } catch (error) {
      console.error('Error asignando agente:', error);
      mostrarNotificacion('Error al asignar agente: ' + error.message, 'error');
    } finally {
      setProcesando(false);
    }
  };

  const handleEditarConcesionaria = async (e) => {
    e.preventDefault();
    
    try {
      if (!concesionariaSeleccionada) return;
      
      setProcesando(true);
      
      const empresaRef = doc(db, 'empresas', concesionariaSeleccionada.id);
      const datosActuales = (await getDoc(empresaRef)).data();
      
      await updateDoc(empresaRef, {
        nombre: formEditarConcesionaria.nombre,
        email: formEditarConcesionaria.email,
        email_empresa: formEditarConcesionaria.email,
        telefono: formEditarConcesionaria.telefono,
        telefono_empresa: formEditarConcesionaria.telefono,
        direccion: formEditarConcesionaria.direccion,
        direccion_empresa: formEditarConcesionaria.direccion,
        activo: formEditarConcesionaria.activo,
        notas_admin: formEditarConcesionaria.notas_admin,
        fechaActualizacion: serverTimestamp(),
        historial_cambios: {
          ...datosActuales?.historial_cambios || {},
          [`edicion_${Date.now()}`]: {
            accion: 'edicion_datos',
            cambios: formEditarConcesionaria,
            fecha: serverTimestamp(),
            usuario: user?.email || 'admin'
          }
        }
      });
      
      mostrarNotificacion('✅ Concesionaria actualizada exitosamente', 'success');
      setMostrandoModalEditarConcesionaria(false);
      await cargarDatos();
      
    } catch (error) {
      console.error('Error editando concesionaria:', error);
      mostrarNotificacion('Error al editar concesionaria: ' + error.message, 'error');
    } finally {
      setProcesando(false);
    }
  };

  const handleToggleActivo = async (concesionariaId, nuevoEstado) => {
    try {
      if (!confirm(`¿${nuevoEstado ? 'Activar' : 'Desactivar'} esta concesionaria?`)) {
        return;
      }

      setProcesando(true);
      
      const empresaRef = doc(db, 'empresas', concesionariaId);
      await updateDoc(empresaRef, {
        activo: nuevoEstado,
        fechaActualizacion: serverTimestamp()
      });
      
      mostrarNotificacion(`✅ Concesionaria ${nuevoEstado ? 'activada' : 'desactivada'} exitosamente`, 'success');
      await cargarDatos();
      
    } catch (error) {
      console.error('Error cambiando estado:', error);
      mostrarNotificacion('Error al cambiar estado: ' + error.message, 'error');
    } finally {
      setProcesando(false);
    }
  };

  const handleAgregarNotas = async () => {
    try {
      if (!concesionariaSeleccionada || !formEditarConcesionaria.notas_admin) return;
      
      setProcesando(true);
      
      const empresaRef = doc(db, 'empresas', concesionariaSeleccionada.id);
      await updateDoc(empresaRef, {
        notas_admin: formEditarConcesionaria.notas_admin,
        fechaActualizacion: serverTimestamp()
      });
      
      mostrarNotificacion('✅ Notas guardadas exitosamente', 'success');
      setMostrandoModalNotas(false);
      await cargarDatos();
      
    } catch (error) {
      console.error('Error guardando notas:', error);
      mostrarNotificacion('Error al guardar notas: ' + error.message, 'error');
    } finally {
      setProcesando(false);
    }
  };

  const handleExportarDatos = () => {
    try {
      const datosExportar = concesionariasFiltradas.map(conc => ({
        Nombre: conc.nombre || '',
        Email: conc.email || conc.email_empresa || '',
        Teléfono: conc.telefono || '',
        Dirección: conc.direccion || '',
        Agente: obtenerAgenteAsignado(conc)?.nombre || 'Sin agente',
        Perfiles_Disponibles: conc.totalPerfilesDisponibles || 0,
        Perfiles_Usados: conc.totalPerfilesUsados || 0,
        Perfiles_Comprados: conc.totalPerfilesComprados || 0,
        Estado: conc.activo !== false ? 'Activa' : 'Inactiva',
        Solicitudes_Lotes_Pendientes: obtenerSolicitudesLotePendientesConcesionaria(conc.id).length
      }));

      const csv = [
        Object.keys(datosExportar[0] || {}).join(','),
        ...datosExportar.map(row => Object.values(row).map(val => `"${val}"`).join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `concesionarias_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      mostrarNotificacion('✅ Datos exportados exitosamente', 'success');
    } catch (error) {
      console.error('Error exportando datos:', error);
      mostrarNotificacion('Error al exportar datos: ' + error.message, 'error');
    }
  };

  const handleBulkAsignarAgente = async (e) => {
    if (e) e.preventDefault();
    
    if (concesionariasSeleccionadas.length === 0) {
      mostrarNotificacion('Selecciona al menos una concesionaria', 'warning');
      return;
    }

    if (!agenteBulkSeleccionado) {
      mostrarNotificacion('Selecciona un agente', 'warning');
      return;
    }

    try {
      setProcesando(true);
      let exitosas = 0;
      let fallidas = 0;

      for (const concId of concesionariasSeleccionadas) {
        try {
          const empresaRef = doc(db, 'empresas', concId);
          await updateDoc(empresaRef, {
            agenteAsignado: agenteBulkSeleccionado,
            agente_id: agenteBulkSeleccionado,
            fechaActualizacion: serverTimestamp()
          });
          exitosas++;
        } catch (error) {
          console.error(`Error asignando agente a ${concId}:`, error);
          fallidas++;
        }
      }

      mostrarNotificacion(
        `✅ ${exitosas} concesionarias actualizadas. ${fallidas > 0 ? `${fallidas} fallaron.` : ''}`,
        exitosas > 0 ? 'success' : 'error'
      );
      
      setConcesionariasSeleccionadas([]);
      setAgenteBulkSeleccionado('');
      setMostrandoModalBulkAsignar(false);
      await cargarDatos();
      
    } catch (error) {
      console.error('Error en asignación masiva:', error);
      mostrarNotificacion('Error en asignación masiva: ' + error.message, 'error');
    } finally {
      setProcesando(false);
    }
  };

  const mostrarNotificacion = (mensaje, tipo = 'info') => {
    const id = Date.now();
    const nuevaNotificacion = { id, mensaje, tipo };
    setNotificaciones(prev => [...prev, nuevaNotificacion]);
    
    setTimeout(() => {
      setNotificaciones(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const abrirModalEditar = (concesionaria) => {
    setConcesionariaSeleccionada(concesionaria);
    setFormEditarConcesionaria({
      nombre: concesionaria.nombre || '',
      email: concesionaria.email || concesionaria.email_empresa || '',
      telefono: concesionaria.telefono || concesionaria.telefono_empresa || '',
      direccion: concesionaria.direccion || concesionaria.direccion_empresa || '',
      activo: concesionaria.activo !== false,
      notas_admin: concesionaria.notas_admin || ''
    });
    setMostrandoModalEditarConcesionaria(true);
  };

  const abrirModalNotas = (concesionaria) => {
    setConcesionariaSeleccionada(concesionaria);
    setFormEditarConcesionaria({
      ...formEditarConcesionaria,
      notas_admin: concesionaria.notas_admin || ''
    });
    setMostrandoModalNotas(true);
  };

  const toggleSeleccionConcesionaria = (concesionariaId) => {
    setConcesionariasSeleccionadas(prev => 
      prev.includes(concesionariaId)
        ? prev.filter(id => id !== concesionariaId)
        : [...prev, concesionariaId]
    );
  };

  const handleVerDetalle = async (concesionaria) => {
    setConcesionariaSeleccionada(concesionaria);
    await cargarLotesConcesionaria(concesionaria.id);
    // Cargar información de perfiles disponibles
    try {
      const disponibles = await ConcesionariaService.calcularPerfilesDisponibles(concesionaria.id);
      setPerfilesDisponiblesInfo(disponibles);
    } catch (error) {
      console.error('Error cargando perfiles disponibles:', error);
      setPerfilesDisponiblesInfo({ disponibles: 0 });
    }
    // Cargar perfiles de vehículos
    await cargarPerfilesConcesionaria(concesionaria.id);
    setMostrandoModalDetalle(true);
  };

  const cargarPerfilesConcesionaria = async (concesionariaId) => {
    setLoadingPerfiles(true);
    try {
      const perfiles = await ConcesionariaService.obtenerPerfilesVehiculos(concesionariaId);
      setPerfilesConcesionaria(perfiles);
    } catch (error) {
      console.error('Error cargando perfiles:', error);
      mostrarNotificacion('Error al cargar perfiles: ' + error.message, 'error');
      setPerfilesConcesionaria([]);
    } finally {
      setLoadingPerfiles(false);
    }
  };

  const handleCrearPerfilVehiculo = async (e) => {
    e.preventDefault();
    
    try {
      if (!concesionariaSeleccionada) {
        mostrarNotificacion('No hay concesionaria seleccionada', 'error');
        return;
      }

      // Validar que hay perfiles disponibles
      if (!perfilesDisponiblesInfo || perfilesDisponiblesInfo.disponibles <= 0) {
        mostrarNotificacion('No hay perfiles disponibles. La concesionaria debe comprar un lote primero.', 'warning');
        return;
      }

      // Validar datos requeridos
      if (!formPerfilVehiculo.marca || !formPerfilVehiculo.modelo || !formPerfilVehiculo.año) {
        mostrarNotificacion('Por favor completa marca, modelo y año', 'warning');
        return;
      }

      setProcesando(true);

      // Crear el perfil
      await ConcesionariaService.crearPerfilVehiculo(concesionariaSeleccionada.id, formPerfilVehiculo);
      
      // Incrementar contador de perfiles usados
      await LotesConcesionariaService.incrementarPerfilesUsados(concesionariaSeleccionada.id);

      mostrarNotificacion('✅ Perfil de vehículo creado exitosamente', 'success');

      // Limpiar formulario
      setFormPerfilVehiculo({
        marca: '',
        modelo: '',
        año: new Date().getFullYear(),
        tipoVehiculo: 'sedan',
        vin: ''
      });
      setMostrandoModalCrearPerfil(false);

      // Recargar información de disponibilidad
      const disponibles = await ConcesionariaService.calcularPerfilesDisponibles(concesionariaSeleccionada.id);
      setPerfilesDisponiblesInfo(disponibles);
      
      // Recargar datos generales
      await cargarDatos();
      
    } catch (error) {
      console.error('Error creando perfil:', error);
      mostrarNotificacion('Error al crear perfil: ' + error.message, 'error');
    } finally {
      setProcesando(false);
    }
  };

  const abrirModalCrearPerfil = async (concesionaria) => {
    setConcesionariaSeleccionada(concesionaria);
    
    // Cargar información de perfiles disponibles
    try {
      const disponibles = await ConcesionariaService.calcularPerfilesDisponibles(concesionaria.id);
      setPerfilesDisponiblesInfo(disponibles);
      
      if (disponibles.disponibles <= 0) {
        mostrarNotificacion('No hay perfiles disponibles. La concesionaria debe comprar un lote primero.', 'warning');
        return;
      }
    } catch (error) {
      console.error('Error cargando perfiles disponibles:', error);
      mostrarNotificacion('Error al verificar disponibilidad de perfiles', 'error');
      return;
    }
    
    setMostrandoModalCrearPerfil(true);
  };

  const obtenerAgenteAsignado = (concesionaria) => {
    const agenteId = concesionaria.agenteAsignado || concesionaria.agente_id;
    if (!agenteId) return null;
    return agentes.find(a => a.id === agenteId);
  };

  const tieneSolicitudPendiente = (concesionariaId) => {
    return solicitudesAgente.some(s => s.concesionariaId === concesionariaId);
  };

  const tieneSolicitudLotePendiente = (concesionariaId) => {
    return solicitudesLotes.some(s => 
      s.concesionariaId === concesionariaId && 
      (s.estado === 'pendiente' || s.estado === 'en_revision')
    );
  };

  const obtenerSolicitudesLoteConcesionaria = (concesionariaId) => {
    return solicitudesLotes.filter(s => s.concesionariaId === concesionariaId);
  };

  const obtenerSolicitudesLotePendientesConcesionaria = (concesionariaId) => {
    return solicitudesLotes.filter(s => 
      s.concesionariaId === concesionariaId && 
      (s.estado === 'pendiente' || s.estado === 'en_revision')
    );
  };

  // Filtrar concesionarias
  const concesionariasFiltradas = concesionarias.filter(conc => {
    const matchBusqueda = !filtroBusqueda || 
      conc.nombre?.toLowerCase().includes(filtroBusqueda.toLowerCase()) ||
      conc.email?.toLowerCase().includes(filtroBusqueda.toLowerCase()) ||
      conc.email_empresa?.toLowerCase().includes(filtroBusqueda.toLowerCase());
    
    const matchEstado = !filtroEstado || 
      (filtroEstado === 'con_agente' && obtenerAgenteAsignado(conc)) ||
      (filtroEstado === 'sin_agente' && !obtenerAgenteAsignado(conc)) ||
      (filtroEstado === 'con_solicitud_lote' && tieneSolicitudLotePendiente(conc.id)) ||
      (filtroEstado === 'activa' && conc.activo !== false);
    
    return matchBusqueda && matchEstado;
  });

  // Estadísticas
  const estadisticas = {
    total: concesionarias.length,
    conAgente: concesionarias.filter(c => obtenerAgenteAsignado(c)).length,
    sinAgente: concesionarias.filter(c => !obtenerAgenteAsignado(c)).length,
    conSolicitudLote: concesionarias.filter(c => tieneSolicitudLotePendiente(c.id)).length,
    solicitudesLotesPendientes: solicitudesLotes.filter(s => s.estado === 'pendiente' || s.estado === 'en_revision').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 relative">
      {/* Sistema de Notificaciones */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notificaciones.map(notif => (
          <div
            key={notif.id}
            className={`px-6 py-4 rounded-lg shadow-xl text-white animate-slide-in-right ${
              notif.tipo === 'success' ? 'bg-green-500' :
              notif.tipo === 'error' ? 'bg-red-500' :
              notif.tipo === 'warning' ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">
                {notif.tipo === 'success' ? '✅' :
                 notif.tipo === 'error' ? '❌' :
                 notif.tipo === 'warning' ? '⚠️' : 'ℹ️'}
              </span>
              <span className="font-medium">{notif.mensaje}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Header Mejorado */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">🚗 Gestión de Concesionarias</h1>
            <p className="text-blue-100">Administra concesionarias, asigna agentes y gestiona lotes de perfiles</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{concesionarias.length}</div>
            <div className="text-blue-100 text-sm">Concesionarias</div>
          </div>
        </div>
      </div>

      {/* Estadísticas Rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{estadisticas.total}</p>
            </div>
            <div className="text-3xl">🚗</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Con Agente</p>
              <p className="text-2xl font-bold text-gray-900">{estadisticas.conAgente}</p>
            </div>
            <div className="text-3xl">👤</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Sin Agente</p>
              <p className="text-2xl font-bold text-gray-900">{estadisticas.sinAgente}</p>
            </div>
            <div className="text-3xl">⚠️</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Solicitudes Lotes</p>
              <p className="text-2xl font-bold text-gray-900">{estadisticas.solicitudesLotesPendientes}</p>
            </div>
            <div className="text-3xl">📦</div>
          </div>
        </div>
      </div>

      {/* Tabs Mejorados */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px px-4">
            <button
              onClick={() => cambiarTab('concesionarias')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors relative ${
                tabActual === 'concesionarias'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Concesionarias ({concesionarias.length})
            </button>
            <button
              onClick={() => cambiarTab('solicitudes')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors relative ${
                tabActual === 'solicitudes'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Solicitudes de Agentes ({solicitudesAgente.length})
              {solicitudesAgente.length > 0 && (
                <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {solicitudesAgente.length}
                </span>
              )}
            </button>
            <button
              onClick={() => cambiarTab('solicitudes-lotes')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors relative ${
                tabActual === 'solicitudes-lotes'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Solicitudes de Lotes ({estadisticas.solicitudesLotesPendientes})
              {estadisticas.solicitudesLotesPendientes > 0 && (
                <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {estadisticas.solicitudesLotesPendientes}
                </span>
              )}
            </button>
            <button
              onClick={() => cambiarTab('clientes')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors relative ${
                tabActual === 'clientes'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Clientes ({clientesConcesionarias.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Tab: Concesionarias - Vista Mejorada con Cards */}
      {tabActual === 'concesionarias' && (
        <div className="space-y-6">
          {/* Filtros y Búsqueda */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <input
                  type="text"
                  placeholder="Buscar por nombre o email..."
                  value={filtroBusqueda}
                  onChange={(e) => setFiltroBusqueda(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los estados</option>
                <option value="con_agente">Con agente asignado</option>
                <option value="sin_agente">Sin agente</option>
                <option value="con_solicitud_lote">Con solicitud de lote</option>
                <option value="activa">Activas</option>
              </select>
              <div className="flex gap-2">
                {concesionariasSeleccionadas.length > 0 && (
                  <button
                    onClick={() => setMostrandoModalBulkAsignar(true)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-all shadow-lg flex items-center gap-2"
                    title={`Asignar agente a ${concesionariasSeleccionadas.length} concesionarias`}
                  >
                    <span>👤</span>
                    <span>Asignar Agente ({concesionariasSeleccionadas.length})</span>
                  </button>
                )}
                <button
                  onClick={handleExportarDatos}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-all shadow-lg flex items-center gap-2"
                >
                  <span>📥</span>
                  <span>Exportar</span>
                </button>
                <button
                  onClick={() => {
                    setFormLote({
                      concesionariaId: '',
                      agenteId: '',
                      cantidad: 50,
                      precioTotal: '',
                      metodoPago: '',
                      notas: ''
                    });
                    setConcesionariaSeleccionada(null);
                    setMostrandoModalCrearLote(true);
                  }}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all shadow-lg flex items-center gap-2"
                >
                  <span>+</span>
                  <span>Crear Lote</span>
                </button>
              </div>
            </div>
            {concesionariasSeleccionadas.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {concesionariasSeleccionadas.length} concesionaria(s) seleccionada(s)
                  </span>
                  <button
                    onClick={() => setConcesionariasSeleccionadas([])}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Limpiar selección
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Grid de Cards */}
          {concesionariasFiltradas.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="text-6xl mb-4">🚗</div>
              <p className="text-gray-500 text-lg">No se encontraron concesionarias</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {concesionariasFiltradas.map(concesionaria => {
                const agenteAsignado = obtenerAgenteAsignado(concesionaria);
                const tieneSolicitud = tieneSolicitudPendiente(concesionaria.id);
                const solicitudesLote = obtenerSolicitudesLoteConcesionaria(concesionaria.id);
                const solicitudesLotePendientes = obtenerSolicitudesLotePendientesConcesionaria(concesionaria.id);
                const solicitudesLoteAprobadas = solicitudesLote.filter(s => s.estado === 'aprobada');
                
                return (
                  <div key={concesionaria.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all border border-gray-200 overflow-hidden">
                    {/* Header del Card */}
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 border-b border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">{concesionaria.nombre}</h3>
                          <p className="text-sm text-gray-600">{concesionaria.email || concesionaria.email_empresa}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          concesionaria.activo !== false 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {concesionaria.activo !== false ? 'Activa' : 'Inactiva'}
                        </span>
                      </div>
                      
                      {/* Badges de alertas */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {tieneSolicitud && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            ⚠️ Solicitud agente
                          </span>
                        )}
                        {solicitudesLotePendientes.length > 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            📦 {solicitudesLotePendientes.length} solicitud{solicitudesLotePendientes.length > 1 ? 'es' : ''} lote
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Contenido del Card */}
                    <div className="p-4 space-y-4">
                      {/* Agente Asignado */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">👤</span>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {agenteAsignado ? agenteAsignado.nombre : 'Sin agente'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {agenteAsignado ? (agenteAsignado.zona_asignada || agenteAsignado.zona || 'Sin zona') : 'Asignar agente'}
                            </p>
                          </div>
                        </div>
                        {!agenteAsignado && (
                          <button
                            onClick={() => {
                              setFormAsignarAgente({
                                concesionariaId: concesionaria.id,
                                agenteId: ''
                              });
                              setConcesionariaSeleccionada(concesionaria);
                              setMostrandoModalAsignarAgente(true);
                            }}
                            className="text-xs px-3 py-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                          >
                            Asignar
                          </button>
                        )}
                      </div>

                      {/* Estadísticas de Perfiles */}
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center p-2 bg-green-50 rounded-lg">
                          <p className="text-lg font-bold text-green-600">{concesionaria.totalPerfilesDisponibles || 0}</p>
                          <p className="text-xs text-gray-600">Disponibles</p>
                        </div>
                        <div className="text-center p-2 bg-blue-50 rounded-lg">
                          <p className="text-lg font-bold text-blue-600">{concesionaria.totalPerfilesUsados || 0}</p>
                          <p className="text-xs text-gray-600">Usados</p>
                        </div>
                        <div className="text-center p-2 bg-purple-50 rounded-lg">
                          <p className="text-lg font-bold text-purple-600">{concesionaria.totalPerfilesComprados || 0}</p>
                          <p className="text-xs text-gray-600">Comprados</p>
                        </div>
                      </div>

                      {/* Solicitudes de Lotes */}
                      {solicitudesLote.length > 0 && (
                        <div className="border-t pt-3">
                          <p className="text-xs font-medium text-gray-700 mb-2">Solicitudes de Lotes:</p>
                          <div className="space-y-1">
                            {solicitudesLotePendientes.length > 0 && (
                              <div className="text-xs bg-yellow-50 p-2 rounded border border-yellow-200">
                                <div className="font-medium text-yellow-900">
                                  ⏳ {solicitudesLotePendientes.length} pendiente{solicitudesLotePendientes.length > 1 ? 's' : ''}
                                </div>
                              </div>
                            )}
                            {solicitudesLoteAprobadas.length > 0 && (
                              <div className="text-xs bg-green-50 p-2 rounded border border-green-200">
                                <div className="font-medium text-green-900">
                                  ✅ {solicitudesLoteAprobadas.length} aprobada{solicitudesLoteAprobadas.length > 1 ? 's' : ''}
                                </div>
                              </div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              Total: {solicitudesLote.length} solicitud{solicitudesLote.length > 1 ? 'es' : ''}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer con Acciones */}
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={concesionariasSeleccionadas.includes(concesionaria.id)}
                            onChange={() => toggleSeleccionConcesionaria(concesionaria.id)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-600">Seleccionar</span>
                        </label>
                        <button
                          onClick={() => handleToggleActivo(concesionaria.id, !(concesionaria.activo !== false))}
                          className={`text-xs px-2 py-1 rounded ${
                            concesionaria.activo !== false
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {concesionaria.activo !== false ? 'Desactivar' : 'Activar'}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleVerDetalle(concesionaria)}
                          className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all"
                        >
                          👁️ Ver Detalle
                        </button>
                        <button
                          onClick={() => abrirModalEditar(concesionaria)}
                          className="px-3 py-2 bg-yellow-600 text-white rounded-lg text-sm font-medium hover:bg-yellow-700 transition-all"
                          title="Editar información"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => abrirModalNotas(concesionaria)}
                          className="px-3 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-all"
                          title="Agregar notas"
                        >
                          📝
                        </button>
                        <button
                          onClick={() => {
                            setFormLote({
                              ...formLote,
                              concesionariaId: concesionaria.id,
                              agenteId: agenteAsignado?.id || ''
                            });
                            setConcesionariaSeleccionada(concesionaria);
                            setMostrandoModalCrearLote(true);
                          }}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-all"
                          title="Crear lote"
                        >
                          📦
                        </button>
                        <button
                          onClick={() => abrirModalCrearPerfil(concesionaria)}
                          disabled={!(concesionaria.totalPerfilesDisponibles > 0)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            concesionaria.totalPerfilesDisponibles > 0
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                          title={concesionaria.totalPerfilesDisponibles > 0 ? 'Crear perfil de vehículo' : 'No hay perfiles disponibles'}
                        >
                          🚗
                        </button>
                        {agenteAsignado && (
                          <button
                            onClick={() => {
                              setFormAsignarAgente({
                                concesionariaId: concesionaria.id,
                                agenteId: agenteAsignado.id
                              });
                              setConcesionariaSeleccionada(concesionaria);
                              setMostrandoModalAsignarAgente(true);
                            }}
                            className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-all"
                            title="Cambiar agente"
                          >
                            👤
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab: Solicitudes de Agentes */}
      {tabActual === 'solicitudes' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Solicitudes de Agentes Pendientes ({solicitudesAgente.length})
            </h2>
          </div>
          {solicitudesAgente.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="text-5xl mb-4">✅</div>
              <p className="text-gray-500 text-lg">No hay solicitudes pendientes</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concesionaria</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motivo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Solicitud</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {solicitudesAgente.map(solicitud => {
                    const concesionaria = concesionarias.find(c => c.id === solicitud.concesionariaId);
                    return (
                      <tr key={solicitud.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {solicitud.concesionariaNombre || concesionaria?.nombre || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {solicitud.concesionariaEmail || concesionaria?.email || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {solicitud.motivo || 'Necesita agente asignado'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatearFecha(solicitud.fechaSolicitud)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              setFormAsignarAgente({
                                concesionariaId: solicitud.concesionariaId,
                                agenteId: ''
                              });
                              setConcesionariaSeleccionada(concesionaria || { id: solicitud.concesionariaId });
                              setMostrandoModalAsignarAgente(true);
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
                          >
                            Asignar Agente
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Solicitudes de Lotes - Mejorado */}
      {tabActual === 'solicitudes-lotes' && (
        <div className="space-y-6">
          {/* Estadísticas Rápidas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pendientes</p>
                  <p className="text-2xl font-bold text-yellow-700">
                    {solicitudesLotes.filter(s => s.estado === 'pendiente' || s.estado === 'en_revision').length}
                  </p>
                </div>
                <div className="text-3xl">⏳</div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Aprobadas</p>
                  <p className="text-2xl font-bold text-green-700">
                    {solicitudesLotes.filter(s => s.estado === 'aprobada').length}
                  </p>
                </div>
                <div className="text-3xl">✅</div>
              </div>
            </div>
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Rechazadas</p>
                  <p className="text-2xl font-bold text-red-700">
                    {solicitudesLotes.filter(s => s.estado === 'rechazada').length}
                  </p>
                </div>
                <div className="text-3xl">❌</div>
              </div>
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {solicitudesLotes.length}
                  </p>
                </div>
                <div className="text-3xl">📦</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                Solicitudes de Lotes ({solicitudesLotes.length})
              </h2>
              <div className="flex gap-2">
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Todos los estados</option>
                  <option value="pendiente">Pendientes</option>
                  <option value="en_revision">En Revisión</option>
                  <option value="aprobada">Aprobadas</option>
                  <option value="rechazada">Rechazadas</option>
                </select>
              </div>
            </div>
            {solicitudesLotes.filter(s => !filtroEstado || s.estado === filtroEstado).length === 0 ? (
              <div className="px-6 py-12 text-center">
                <div className="text-5xl mb-4">📦</div>
                <p className="text-gray-500 text-lg">No hay solicitudes de lotes</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concesionaria</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lote</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agente</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Solicitud</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Atención</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Atendido Por</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {solicitudesLotes
                      .filter(s => !filtroEstado || s.estado === filtroEstado)
                      .sort((a, b) => {
                        // Ordenar: pendientes primero, luego por fecha
                        const estadoA = a.estado === 'pendiente' || a.estado === 'en_revision' ? 0 : 1;
                        const estadoB = b.estado === 'pendiente' || b.estado === 'en_revision' ? 0 : 1;
                        if (estadoA !== estadoB) return estadoA - estadoB;
                        
                        const fechaA = a.fechaSolicitud?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
                        const fechaB = b.fechaSolicitud?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
                        return fechaB - fechaA;
                      })
                      .map((solicitud) => {
                        const concesionaria = concesionarias.find(c => c.id === solicitud.concesionariaId);
                        const esAtendida = solicitud.estado === 'aprobada' || solicitud.estado === 'rechazada';
                        const esPendiente = solicitud.estado === 'pendiente' || solicitud.estado === 'en_revision';
                        
                        return (
                          <tr 
                            key={solicitud.id} 
                            className={`${
                              esAtendida 
                                ? 'bg-gray-50 opacity-75' 
                                : esPendiente 
                                ? 'bg-white hover:bg-yellow-50' 
                                : 'hover:bg-gray-50'
                            } transition-all`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`text-sm font-medium ${esAtendida ? 'text-gray-600' : 'text-gray-900'}`}>
                                {solicitud.concesionariaNombre || concesionaria?.nombre || 'N/A'}
                              </div>
                              <div className={`text-sm ${esAtendida ? 'text-gray-400' : 'text-gray-500'}`}>
                                {solicitud.concesionariaEmail}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`text-sm font-medium ${esAtendida ? 'text-gray-600' : 'text-gray-900'}`}>
                                {solicitud.nombreLote}
                              </div>
                              <div className={`text-xs ${esAtendida ? 'text-gray-400' : 'text-gray-500'}`}>
                                {solicitud.tipoLote}
                              </div>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${esAtendida ? 'text-gray-600' : 'text-gray-900'}`}>
                              {solicitud.cantidad} perfiles
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${esAtendida ? 'text-gray-600' : 'text-gray-900'}`}>
                              ${solicitud.precio?.toLocaleString('es-CL') || '0'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${esAtendida ? 'text-gray-600' : 'text-gray-900'}`}>
                              {solicitud.agenteNombre || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                solicitud.estado === 'pendiente' || solicitud.estado === 'en_revision'
                                  ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300'
                                  : solicitud.estado === 'aprobada'
                                  ? 'bg-green-100 text-green-800 border-2 border-green-300'
                                  : solicitud.estado === 'rechazada'
                                  ? 'bg-red-100 text-red-800 border-2 border-red-300'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {solicitud.estado === 'pendiente' ? '⏳ Pendiente' :
                                 solicitud.estado === 'en_revision' ? '🔍 En Revisión' :
                                 solicitud.estado === 'aprobada' ? '✅ Aprobada' :
                                 solicitud.estado === 'rechazada' ? '❌ Rechazada' : solicitud.estado}
                              </span>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${esAtendida ? 'text-gray-500' : 'text-gray-700'}`}>
                              {solicitud.fechaSolicitud?.toDate 
                                ? solicitud.fechaSolicitud.toDate().toLocaleDateString('es-CL')
                                : solicitud.fechaCreacion 
                                ? new Date(solicitud.fechaCreacion).toLocaleDateString('es-CL')
                                : 'N/A'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${esAtendida ? 'text-gray-500' : 'text-gray-400'}`}>
                              {esAtendida ? (
                                solicitud.fechaAprobacion?.toDate 
                                  ? solicitud.fechaAprobacion.toDate().toLocaleDateString('es-CL')
                                  : solicitud.fechaRechazo?.toDate
                                  ? solicitud.fechaRechazo.toDate().toLocaleDateString('es-CL')
                                  : 'N/A'
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${esAtendida ? 'text-gray-500' : 'text-gray-400'}`}>
                              {esAtendida ? (
                                <div>
                                  <div className="font-medium">
                                    {solicitud.aprobadoPor || solicitud.rechazadoPor || 'Admin'}
                                  </div>
                                  {solicitud.motivoRechazo && (
                                    <div className="text-xs text-red-600 mt-1">
                                      Motivo: {solicitud.motivoRechazo}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              {esPendiente && (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      setSolicitudLoteSeleccionada(solicitud);
                                      setMostrandoModalGestionLote(true);
                                    }}
                                    className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-all shadow-md"
                                  >
                                    Gestionar
                                  </button>
                                </div>
                              )}
                              {esAtendida && (
                                <div className="flex flex-col gap-1">
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    solicitud.estado === 'aprobada' 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {solicitud.estado === 'aprobada' ? '✅ Atendida' : '❌ Rechazada'}
                                  </span>
                                  {solicitud.loteId && solicitud.loteId !== 'creado' && (
                                    <span className="text-xs text-gray-500">
                                      Lote: {solicitud.loteId}
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Crear Lote - Mejorado */}
      {mostrandoModalCrearLote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">📦 Crear Lote de Perfiles</h2>
                  {concesionariaSeleccionada && (
                    <p className="text-gray-600 mt-1">
                      Para: <strong>{concesionariaSeleccionada.nombre}</strong>
                    </p>
                  )}
                  <p className="text-sm text-blue-600 mt-1">
                    ⚡ Como administrador, puedes crear lotes sin necesidad de agente asignado
                  </p>
                </div>
                <button
                  onClick={() => {
                    setMostrandoModalCrearLote(false);
                    setConcesionariaSeleccionada(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCrearLote} className="space-y-4">
                {!concesionariaSeleccionada && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Seleccionar Concesionaria *
                    </label>
                    <select
                      value={formLote.concesionariaId}
                      onChange={(e) => setFormLote({ ...formLote, concesionariaId: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecciona una concesionaria</option>
                      {concesionarias.map(conc => (
                        <option key={conc.id} value={conc.id}>
                          {conc.nombre} - {conc.email || conc.email_empresa}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agente Vendedor (Opcional)
                  </label>
                  <select
                    value={formLote.agenteId}
                    onChange={(e) => setFormLote({ ...formLote, agenteId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sin agente (Admin)</option>
                    {agentes.map(agente => (
                      <option key={agente.id} value={agente.id}>
                        {agente.nombre || 'Sin nombre'} - {agente.zona_asignada || agente.zona || 'Sin zona'}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Si no seleccionas un agente, el lote se asignará al sistema (Admin)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cantidad de Perfiles *
                  </label>
                  <div className="grid grid-cols-5 gap-2 mb-2">
                    {[10, 25, 50, 100, 200].map(cant => (
                      <button
                        key={cant}
                        type="button"
                        onClick={() => setFormLote({ ...formLote, cantidad: cant })}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          formLote.cantidad === cant
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {cant}
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    min="1"
                    value={formLote.cantidad}
                    onChange={(e) => setFormLote({ ...formLote, cantidad: parseInt(e.target.value) || 50 })}
                    placeholder="O ingresa cantidad personalizada"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Precio Total (Opcional)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formLote.precioTotal}
                      onChange={(e) => setFormLote({ ...formLote, precioTotal: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ej: 5000000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Método de Pago (Opcional)
                    </label>
                    <select
                      value={formLote.metodoPago}
                      onChange={(e) => setFormLote({ ...formLote, metodoPago: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar método</option>
                      <option value="transferencia">Transferencia</option>
                      <option value="efectivo">Efectivo</option>
                      <option value="cheque">Cheque</option>
                      <option value="tarjeta">Tarjeta</option>
                      <option value="credito">Crédito</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notas (Opcional)
                  </label>
                  <textarea
                    value={formLote.notas}
                    onChange={(e) => setFormLote({ ...formLote, notas: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Notas adicionales sobre el lote..."
                  />
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="submit"
                    disabled={procesando}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {procesando ? 'Creando...' : '✅ Crear Lote'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMostrandoModalCrearLote(false);
                      setConcesionariaSeleccionada(null);
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestionar Solicitud de Lote */}
      {mostrandoModalGestionLote && solicitudLoteSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">📦 Gestionar Solicitud de Lote</h2>
                  <p className="text-gray-600 mt-1">
                    {solicitudLoteSeleccionada.concesionariaNombre}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setMostrandoModalGestionLote(false);
                    setSolicitudLoteSeleccionada(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Detalles de la Solicitud</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Concesionaria:</p>
                      <p className="font-medium text-gray-900">{solicitudLoteSeleccionada.concesionariaNombre}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Email:</p>
                      <p className="font-medium text-gray-900">{solicitudLoteSeleccionada.concesionariaEmail}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Lote:</p>
                      <p className="font-medium text-gray-900">{solicitudLoteSeleccionada.nombreLote}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Cantidad:</p>
                      <p className="font-medium text-gray-900">{solicitudLoteSeleccionada.cantidad} perfiles</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Precio:</p>
                      <p className="font-medium text-gray-900">${solicitudLoteSeleccionada.precio?.toLocaleString('es-CL') || '0'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Agente:</p>
                      <p className="font-medium text-gray-900">{solicitudLoteSeleccionada.agenteNombre || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Fecha Solicitud:</p>
                      <p className="font-medium text-gray-900">
                        {solicitudLoteSeleccionada.fechaSolicitud?.toDate 
                          ? solicitudLoteSeleccionada.fechaSolicitud.toDate().toLocaleString('es-CL')
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Estado:</p>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        solicitudLoteSeleccionada.estado === 'pendiente' || solicitudLoteSeleccionada.estado === 'en_revision'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {solicitudLoteSeleccionada.estado === 'pendiente' ? 'Pendiente' :
                         solicitudLoteSeleccionada.estado === 'en_revision' ? 'En Revisión' :
                         solicitudLoteSeleccionada.estado}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => handleAprobarSolicitudLote(solicitudLoteSeleccionada)}
                    disabled={procesando}
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {procesando ? 'Procesando...' : '✅ Aprobar y Crear Lote'}
                  </button>
                  <button
                    onClick={() => handleRechazarSolicitudLote(solicitudLoteSeleccionada)}
                    disabled={procesando}
                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {procesando ? 'Procesando...' : '❌ Rechazar'}
                  </button>
                  <button
                    onClick={() => {
                      setMostrandoModalGestionLote(false);
                      setSolicitudLoteSeleccionada(null);
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Asignar Agente */}
      {mostrandoModalAsignarAgente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">👤 Asignar Agente</h2>
                <button
                  onClick={() => setMostrandoModalAsignarAgente(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {concesionariaSeleccionada && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Concesionaria:</strong> {concesionariaSeleccionada.nombre}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Email:</strong> {concesionariaSeleccionada.email || concesionariaSeleccionada.email_empresa}
                  </p>
                </div>
              )}

              <form onSubmit={handleAsignarAgente} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar Agente *
                  </label>
                  <select
                    value={formAsignarAgente.agenteId}
                    onChange={(e) => setFormAsignarAgente({ ...formAsignarAgente, agenteId: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecciona un agente</option>
                    {agentes.map(agente => (
                      <option key={agente.id} value={agente.id}>
                        {agente.nombre || 'Sin nombre'} - {agente.zona_asignada || agente.zona || 'Sin zona'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="submit"
                    disabled={procesando}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {procesando ? 'Asignando...' : '✅ Asignar Agente'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMostrandoModalAsignarAgente(false)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle Concesionaria - Mejorado */}
      {mostrandoModalDetalle && concesionariaSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    🚗 Detalle: {concesionariaSeleccionada.nombre}
                  </h2>
                  <p className="text-gray-600 mt-1">{concesionariaSeleccionada.email || concesionariaSeleccionada.email_empresa}</p>
                </div>
                <button
                  onClick={() => {
                    setMostrandoModalDetalle(false);
                    setConcesionariaSeleccionada(null);
                    setLotesConcesionaria([]);
                    setPerfilesConcesionaria([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Información General - Mejorada */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span>📋</span>
                    <span>Información General</span>
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><strong className="text-gray-700">Nombre:</strong> <span className="text-gray-900">{concesionariaSeleccionada.nombre}</span></p>
                    <p><strong className="text-gray-700">Email:</strong> <span className="text-gray-900">{concesionariaSeleccionada.email || concesionariaSeleccionada.email_empresa}</span></p>
                    <p><strong className="text-gray-700">Teléfono:</strong> <span className="text-gray-900">{concesionariaSeleccionada.telefono || 'N/A'}</span></p>
                    <p><strong className="text-gray-700">Dirección:</strong> <span className="text-gray-900">{concesionariaSeleccionada.direccion || 'N/A'}</span></p>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span>📊</span>
                    <span>Estadísticas de Perfiles</span>
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Disponibles:</span>
                      <span className="font-bold text-green-600 text-lg">{concesionariaSeleccionada.totalPerfilesDisponibles || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Usados:</span>
                      <span className="font-bold text-gray-900">{concesionariaSeleccionada.totalPerfilesUsados || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Comprados:</span>
                      <span className="font-bold text-gray-900">{concesionariaSeleccionada.totalPerfilesComprados || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span>👤</span>
                    <span>Agente Asignado</span>
                  </h3>
                  {obtenerAgenteAsignado(concesionariaSeleccionada) ? (
                    <div className="space-y-2 text-sm">
                      <p><strong className="text-gray-700">Nombre:</strong> <span className="text-gray-900">{obtenerAgenteAsignado(concesionariaSeleccionada).nombre}</span></p>
                      <p><strong className="text-gray-700">Zona:</strong> <span className="text-gray-900">{obtenerAgenteAsignado(concesionariaSeleccionada).zona_asignada || obtenerAgenteAsignado(concesionariaSeleccionada).zona || 'N/A'}</span></p>
                      <p><strong className="text-gray-700">Email:</strong> <span className="text-gray-900">{obtenerAgenteAsignado(concesionariaSeleccionada).email || 'N/A'}</span></p>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-red-600 font-medium mb-2">Sin agente asignado</p>
                      <button
                        onClick={() => {
                          setFormAsignarAgente({
                            concesionariaId: concesionariaSeleccionada.id,
                            agenteId: ''
                          });
                          setMostrandoModalAsignarAgente(true);
                          setMostrandoModalDetalle(false);
                        }}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                      >
                        Asignar Agente
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Información de Perfiles Disponibles */}
              {perfilesDisponiblesInfo && (
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 mb-6 border border-indigo-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span>🚗</span>
                    <span>Perfiles de Vehículos Disponibles</span>
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{perfilesDisponiblesInfo.disponibles || 0}</p>
                      <p className="text-xs text-gray-600">Disponibles</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-600">{perfilesDisponiblesInfo.comprados || 0}</p>
                      <p className="text-xs text-gray-600">Comprados</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-orange-600">{perfilesDisponiblesInfo.usados || 0}</p>
                      <p className="text-xs text-gray-600">Usados</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-600">{perfilesDisponiblesInfo.preCreados || 0}</p>
                      <p className="text-xs text-gray-600">Pre-Creados</p>
                    </div>
                  </div>
                  {perfilesDisponiblesInfo.disponibles > 0 && (
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          setMostrandoModalDetalle(false);
                          abrirModalCrearPerfil(concesionariaSeleccionada);
                        }}
                        className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-all shadow-lg flex items-center justify-center gap-2"
                      >
                        <span>+</span>
                        <span>Crear Nuevo Perfil de Vehículo</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Lotes - Mejorado */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span>📦</span>
                    <span>Lotes de Perfiles ({lotesConcesionaria.length})</span>
                  </h3>
                  <button
                    onClick={() => {
                      setFormLote({
                        ...formLote,
                        concesionariaId: concesionariaSeleccionada.id,
                        agenteId: obtenerAgenteAsignado(concesionariaSeleccionada)?.id || ''
                      });
                      setMostrandoModalCrearLote(true);
                      setMostrandoModalDetalle(false);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-all shadow-lg flex items-center gap-2"
                  >
                    <span>+</span>
                    <span>Crear Nuevo Lote</span>
                  </button>
                </div>
                {lotesConcesionaria.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <div className="text-5xl mb-4">📦</div>
                    <p className="text-gray-500 text-lg">No hay lotes registrados</p>
                    <p className="text-gray-400 text-sm mt-2">Crea el primer lote para esta concesionaria</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Disponibles</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usados</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agente</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {lotesConcesionaria.map(lote => (
                          <tr key={lote.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatearFecha(lote.fechaCreacion)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {lote.cantidad}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 font-bold">
                              {lote.cantidadDisponible || 0}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {lote.cantidadUsada || 0}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {lote.precioTotal ? `$${lote.precioTotal.toLocaleString('es-CL')}` : 'N/A'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {lote.agenteNombre || 'Admin Sistema'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                lote.activo && lote.cantidadDisponible > 0
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {lote.activo && lote.cantidadDisponible > 0 ? 'Activo' : 'Agotado'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Perfiles de Vehículos Creados */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span>🚗</span>
                    <span>Perfiles de Vehículos Creados ({perfilesConcesionaria.length})</span>
                  </h3>
                  <button
                    onClick={() => cargarPerfilesConcesionaria(concesionariaSeleccionada.id)}
                    disabled={loadingPerfiles}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    <span>🔄</span>
                    <span>{loadingPerfiles ? 'Cargando...' : 'Actualizar'}</span>
                  </button>
                </div>
                {loadingPerfiles ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <div className="text-gray-500">Cargando perfiles...</div>
                  </div>
                ) : perfilesConcesionaria.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <div className="text-5xl mb-4">🚗</div>
                    <p className="text-gray-500 text-lg">No hay perfiles de vehículos creados</p>
                    <p className="text-gray-400 text-sm mt-2">Crea el primer perfil desde el botón de arriba</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehículo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Premium</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Creación</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {perfilesConcesionaria.map(perfil => (
                          <tr key={perfil.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {perfil.marca} {perfil.modelo}
                              </div>
                              <div className="text-xs text-gray-500">
                                Año: {perfil.año || 'N/A'} | VIN: {perfil.vin || 'N/A'}
                              </div>
                              {perfil.tipoVehiculo && (
                                <div className="text-xs text-gray-400">
                                  Tipo: {perfil.tipoVehiculo}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {perfil.clienteNombre ? (
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{perfil.clienteNombre}</div>
                                  <div className="text-xs text-gray-500">{perfil.clienteEmail || ''}</div>
                                  {perfil.clienteTelefono && (
                                    <div className="text-xs text-gray-400">{perfil.clienteTelefono}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">Sin asignar</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                COLOR_ESTADO_PERFIL[perfil.estado] || 'bg-gray-100 text-gray-800'
                              }`}>
                                {DESCRIPCION_ESTADO_PERFIL[perfil.estado] || perfil.estado || 'N/A'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {perfil.membresiaPremiumActiva ? (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                  ⭐ Premium Activo
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                  Básico
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {perfil.fechaCreacion ? formatearFecha(perfil.fechaCreacion) : 'N/A'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                              <div className="flex flex-col gap-2">
                                {perfil.estado === ESTADO_PERFIL_VEHICULO.PENDIENTE_VALIDACION && perfil.clienteId ? (
                                  <>
                                    <button
                                      onClick={() => {
                                        window.open(`/dashboard/cliente/${perfil.clienteId}?modo=validacion&perfilId=${perfil.id}`, '_blank');
                                      }}
                                      className="text-blue-600 hover:text-blue-900 font-semibold text-xs"
                                    >
                                      🔍 Validar Perfil
                                    </button>
                                    <button
                                      onClick={async () => {
                                        if (!confirm(`¿Confirmar que el perfil ${perfil.marca} ${perfil.modelo} está OK y activarlo?`)) {
                                          return;
                                        }
                                        try {
                                          setProcesando(true);
                                          await ConcesionariaService.validarPerfilVehiculo(perfil.id);
                                          mostrarNotificacion('✅ Perfil validado y activado exitosamente', 'success');
                                          await cargarPerfilesConcesionaria(concesionariaSeleccionada.id);
                                        } catch (error) {
                                          console.error('Error validando perfil:', error);
                                          mostrarNotificacion('Error al validar perfil: ' + error.message, 'error');
                                        } finally {
                                          setProcesando(false);
                                        }
                                      }}
                                      className="text-green-600 hover:text-green-900 font-semibold text-xs"
                                      disabled={procesando}
                                    >
                                      ✅ Confirmar OK
                                    </button>
                                  </>
                                ) : perfil.clienteId ? (
                                  <button
                                    onClick={() => {
                                      window.open(`/dashboard/cliente/${perfil.clienteId}`, '_blank');
                                    }}
                                    className="text-blue-600 hover:text-blue-900 font-semibold text-xs"
                                  >
                                    👁️ Ver Perfil Cliente
                                  </button>
                                ) : (
                                  <span className="text-xs text-gray-400">Sin cliente asignado</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Solicitudes de Lotes Pendientes */}
              {obtenerSolicitudesLotePendientesConcesionaria(concesionariaSeleccionada.id).length > 0 && (
                <div className="mt-6 bg-yellow-50 rounded-xl border border-yellow-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span>⏳</span>
                    <span>Solicitudes de Lotes Pendientes</span>
                  </h3>
                  <div className="space-y-3">
                    {obtenerSolicitudesLotePendientesConcesionaria(concesionariaSeleccionada.id).map(sol => (
                      <div key={sol.id} className="bg-white rounded-lg p-4 border border-yellow-300">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{sol.nombreLote || 'Lote sin nombre'}</p>
                            <p className="text-sm text-gray-600">
                              {sol.cantidad} perfiles - ${sol.precio?.toLocaleString('es-CL') || '0'}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Fecha: {sol.fechaSolicitud?.toDate 
                                ? sol.fechaSolicitud.toDate().toLocaleDateString('es-CL')
                                : 'N/A'}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setSolicitudLoteSeleccionada(sol);
                              setMostrandoModalGestionLote(true);
                              setMostrandoModalDetalle(false);
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                          >
                            Gestionar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Concesionaria */}
      {mostrandoModalEditarConcesionaria && concesionariaSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">✏️ Editar Concesionaria</h2>
                <button
                  onClick={() => setMostrandoModalEditarConcesionaria(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleEditarConcesionaria} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      value={formEditarConcesionaria.nombre}
                      onChange={(e) => setFormEditarConcesionaria({ ...formEditarConcesionaria, nombre: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formEditarConcesionaria.email}
                      onChange={(e) => setFormEditarConcesionaria({ ...formEditarConcesionaria, email: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={formEditarConcesionaria.telefono}
                      onChange={(e) => setFormEditarConcesionaria({ ...formEditarConcesionaria, telefono: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dirección
                    </label>
                    <input
                      type="text"
                      value={formEditarConcesionaria.direccion}
                      onChange={(e) => setFormEditarConcesionaria({ ...formEditarConcesionaria, direccion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <select
                    value={formEditarConcesionaria.activo ? 'activa' : 'inactiva'}
                    onChange={(e) => setFormEditarConcesionaria({ ...formEditarConcesionaria, activo: e.target.value === 'activa' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="activa">Activa</option>
                    <option value="inactiva">Inactiva</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notas Administrativas
                  </label>
                  <textarea
                    value={formEditarConcesionaria.notas_admin}
                    onChange={(e) => setFormEditarConcesionaria({ ...formEditarConcesionaria, notas_admin: e.target.value })}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Notas internas sobre esta concesionaria..."
                  />
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="submit"
                    disabled={procesando}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {procesando ? 'Guardando...' : '✅ Guardar Cambios'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMostrandoModalEditarConcesionaria(false)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Agregar Notas */}
      {mostrandoModalNotas && concesionariaSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">📝 Notas Administrativas</h2>
                  <p className="text-gray-600 mt-1">{concesionariaSeleccionada.nombre}</p>
                </div>
                <button
                  onClick={() => setMostrandoModalNotas(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notas
                  </label>
                  <textarea
                    value={formEditarConcesionaria.notas_admin}
                    onChange={(e) => setFormEditarConcesionaria({ ...formEditarConcesionaria, notas_admin: e.target.value })}
                    rows="8"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Agrega notas administrativas sobre esta concesionaria..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Estas notas son solo visibles para administradores
                  </p>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={handleAgregarNotas}
                    disabled={procesando}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {procesando ? 'Guardando...' : '✅ Guardar Notas'}
                  </button>
                  <button
                    onClick={() => setMostrandoModalNotas(false)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Asignación Masiva de Agente */}
      {mostrandoModalBulkAsignar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">👤 Asignación Masiva de Agente</h2>
                  <p className="text-gray-600 mt-1">
                    {concesionariasSeleccionadas.length} concesionaria(s) seleccionada(s)
                  </p>
                </div>
                <button
                  onClick={() => {
                    setMostrandoModalBulkAsignar(false);
                    setAgenteBulkSeleccionado('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleBulkAsignarAgente} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar Agente *
                  </label>
                  <select
                    value={agenteBulkSeleccionado}
                    onChange={(e) => setAgenteBulkSeleccionado(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecciona un agente</option>
                    {agentes.map(agente => (
                      <option key={agente.id} value={agente.id}>
                        {agente.nombre || 'Sin nombre'} - {agente.zona_asignada || agente.zona || 'Sin zona'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Esta acción asignará el agente seleccionado a todas las concesionarias marcadas.
                    ¿Estás seguro de continuar?
                  </p>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="submit"
                    disabled={procesando || !agenteBulkSeleccionado}
                    className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {procesando ? 'Asignando...' : `✅ Asignar a ${concesionariasSeleccionadas.length} concesionarias`}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMostrandoModalBulkAsignar(false);
                      setAgenteBulkSeleccionado('');
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Clientes de Concesionarias */}
      {tabActual === 'clientes' && (
        <div className="space-y-6">
          {/* Header y Estadísticas */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">👥 Clientes Asociados a Concesionarias</h2>
                <p className="text-gray-600 mt-1">
                  Visualiza todos los clientes que tienen perfiles premium generados por concesionarias
                </p>
              </div>
              <button
                onClick={cargarClientesConcesionarias}
                disabled={loadingClientes}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50"
              >
                {loadingClientes ? 'Cargando...' : '🔄 Actualizar'}
              </button>
            </div>

            {/* Estadísticas rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Clientes</p>
                    <p className="text-2xl font-bold text-blue-700">{clientesConcesionarias.length}</p>
                  </div>
                  <div className="text-3xl">👥</div>
                </div>
              </div>
              <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Con Perfil Premium</p>
                    <p className="text-2xl font-bold text-green-700">
                      {clientesConcesionarias.filter(c => c.membresiaPremiumActiva || c.tienePerfilPremium).length}
                    </p>
                  </div>
                  <div className="text-3xl">⭐</div>
                </div>
              </div>
              <div className="bg-purple-50 border-l-4 border-purple-500 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Concesionarias</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {new Set(clientesConcesionarias.map(c => c.concesionariaId)).size}
                    </p>
                  </div>
                  <div className="text-3xl">🏢</div>
                </div>
              </div>
              <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Con Vehículos</p>
                    <p className="text-2xl font-bold text-orange-700">
                      {clientesConcesionarias.filter(c => c.marca && c.modelo).length}
                    </p>
                  </div>
                  <div className="text-3xl">🚗</div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de Clientes */}
          {loadingClientes ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="text-gray-500">Cargando clientes...</div>
            </div>
          ) : clientesConcesionarias.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="text-gray-500 text-lg">
                No hay clientes asociados a concesionarias aún.
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contacto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Concesionaria
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vehículo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado Premium
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha Asignación
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clientesConcesionarias.map((cliente, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-semibold">
                                {(cliente.clienteNombre || 'C').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {cliente.clienteNombre || 'Sin nombre'}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {cliente.clienteId?.substring(0, 8)}...
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{cliente.clienteEmail || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{cliente.clienteTelefono || 'Sin teléfono'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {cliente.concesionariaNombre || 'Sin nombre'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {cliente.marca && cliente.modelo ? (
                            <div className="text-sm text-gray-900">
                              {cliente.marca} {cliente.modelo}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">Sin vehículo asignado</span>
                          )}
                          {cliente.año && (
                            <div className="text-xs text-gray-500">Año: {cliente.año}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(cliente.membresiaPremiumActiva || cliente.tienePerfilPremium || cliente.plan === 'premium') ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ⭐ Premium Activo
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Básico
                            </span>
                          )}
                          {cliente.estadoPerfil && (
                            <div className="text-xs text-gray-500 mt-1">
                              Estado: {cliente.estadoPerfil}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {cliente.fechaAsignacion ? (
                            formatearFecha(cliente.fechaAsignacion)
                          ) : cliente.fechaRegistro ? (
                            formatearFecha(cliente.fechaRegistro)
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              if (cliente.clienteId) {
                                window.open(`/dashboard/cliente/${cliente.clienteId}`, '_blank');
                              } else {
                                mostrarNotificacion('No se pudo abrir el perfil del cliente. ID no disponible.', 'error');
                              }
                            }}
                            className="text-blue-600 hover:text-blue-900 font-semibold"
                          >
                            👁️ Ver Perfil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Crear Perfil de Vehículo */}
      {mostrandoModalCrearPerfil && concesionariaSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">🚗 Crear Perfil de Vehículo</h2>
                  <p className="text-gray-600 mt-1">
                    Para: <strong>{concesionariaSeleccionada.nombre}</strong>
                  </p>
                  {perfilesDisponiblesInfo && (
                    <p className={`text-sm mt-2 font-medium ${
                      perfilesDisponiblesInfo.disponibles > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {perfilesDisponiblesInfo.disponibles > 0 
                        ? `✅ ${perfilesDisponiblesInfo.disponibles} perfil(es) disponible(s)`
                        : '❌ No hay perfiles disponibles. Debe comprar un lote primero.'}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setMostrandoModalCrearPerfil(false);
                    setConcesionariaSeleccionada(null);
                    setPerfilesDisponiblesInfo(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCrearPerfilVehiculo} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Marca *
                    </label>
                    <input
                      type="text"
                      value={formPerfilVehiculo.marca}
                      onChange={(e) => setFormPerfilVehiculo({ ...formPerfilVehiculo, marca: e.target.value })}
                      required
                      placeholder="Ej: Toyota, Ford, Chevrolet"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Modelo *
                    </label>
                    <input
                      type="text"
                      value={formPerfilVehiculo.modelo}
                      onChange={(e) => setFormPerfilVehiculo({ ...formPerfilVehiculo, modelo: e.target.value })}
                      required
                      placeholder="Ej: Corolla, F-150, Silverado"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Año *
                    </label>
                    <input
                      type="number"
                      min="1900"
                      max={new Date().getFullYear() + 1}
                      value={formPerfilVehiculo.año}
                      onChange={(e) => setFormPerfilVehiculo({ ...formPerfilVehiculo, año: parseInt(e.target.value) || new Date().getFullYear() })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Vehículo *
                    </label>
                    <select
                      value={formPerfilVehiculo.tipoVehiculo}
                      onChange={(e) => setFormPerfilVehiculo({ ...formPerfilVehiculo, tipoVehiculo: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(TIPO_VEHICULO).map(([key, value]) => (
                        <option key={key} value={value}>
                          {value.charAt(0).toUpperCase() + value.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    VIN (Número de Identificación del Vehículo) - Opcional
                  </label>
                  <input
                    type="text"
                    value={formPerfilVehiculo.vin}
                    onChange={(e) => setFormPerfilVehiculo({ ...formPerfilVehiculo, vin: e.target.value })}
                    placeholder="Ej: 1HGBH41JXMN109186"
                    maxLength={17}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    El VIN es un código único de 17 caracteres que identifica cada vehículo
                  </p>
                </div>

                {perfilesDisponiblesInfo && perfilesDisponiblesInfo.disponibles > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Información:</strong> Al crear este perfil, se utilizará 1 perfil de los {perfilesDisponiblesInfo.disponibles} disponibles.
                      El perfil quedará en estado "Pre-Creado" y podrá ser asignado a un cliente posteriormente.
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="submit"
                    disabled={procesando || !perfilesDisponiblesInfo || perfilesDisponiblesInfo.disponibles <= 0}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {procesando ? 'Creando...' : '✅ Crear Perfil de Vehículo'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMostrandoModalCrearPerfil(false);
                      setConcesionariaSeleccionada(null);
                      setPerfilesDisponiblesInfo(null);
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
